import { lookupProperty, millisToDuration, DurationPrecision, durationToStrings } from '../util/util';
import { Datum } from '../data-source/types';
import { ColumnDefinition, DatumPropertyDefinition } from '../observation/observation';
import { getDateTimeFormat, timeSeparatorSymbol, subSecondSeparatorSymbol } from '../application/i18n';
import { CellComponent, EmptyCallback, ColumnDefinition as TabulatorColumnDefinition  } from 'tabulator-tables';


export const createDatumPropertyDefinition = (column: ColumnDefinition, columnMap: { [key: string]: ColumnDefinition }, referencedColumns?: string[]): DatumPropertyDefinition => {
  let columnType = getColumnType(column);
  let valueGetter: (datum: Datum) => any;
  let valueFormatter: (datum: Datum) => string;
  let valueExporter: (datum: Datum) => any;
  if (columnType === 'dependent') {
    var columnMappers: { [key: string]: DatumPropertyDefinition } = {};
    Object.entries(column.probeOptions.table.valueToDependentKeyMap).forEach(entry => {
      referencedColumns = referencedColumns ? referencedColumns : [];
      if (!referencedColumns.includes(entry[1])) {
        columnMappers[entry[0]] = createDatumPropertyDefinition(columnMap[entry[1]], columnMap, referencedColumns.concat([column.field]));
      }
    });
    // add the mappers to the options
    column.probeOptions.table.mappers = columnMappers;
    
    valueGetter = valueGetFactories['dependent'](column);
    valueFormatter = valueDisplayFactories['dependent'](column);
    valueExporter = valueFormatter;
  } else {
    valueGetter = valueGetFactories['any'](column);
    valueFormatter = valueDisplayFactories[columnType](column);
    valueExporter = valueExportFactories[columnType](column);
  }
  let tabulatorColumnDefinition = tabulatorColumnDefinitionFactories[columnType](column, valueFormatter);

  return { column: column, type: columnType, value: valueGetter, display: valueFormatter, export: valueExporter, tabulator: tabulatorColumnDefinition };
}

const getColumnType = (column: ColumnDefinition, forceAuto?: boolean): string => {
  if (forceAuto === true || column.probeOptions.table.render === 'auto') {
    if (column.probeType.toLowerCase().startsWith("datetime")) {
      return 'instant';
    } else if (column.probeType.toLowerCase().startsWith("duration")) {
      return 'duration';
    } else if (column.probeType === 'boolean') {
      return 'boolean';
    } else if (column.enumLabels) {
      return 'enum';
    } else {
      return 'any';
    }
  } else if (column.probeOptions.table.render === 'mapDependent') {
    return 'dependent'
  } else {
    return 'any';
  }
}

const NO_CONTENT_LABEL: string = '&mdash;';

export const valueGetFactories: { [key: string]: (column: ColumnDefinition) => (datum: Datum) => any } = {
  'any': (column: ColumnDefinition): (datum: Datum) => any => {
    return (datum: Datum): any => lookupProperty(datum, column.field);
  },
  'dependent': (column: ColumnDefinition): (datum: Datum) => any => {
    return (datum: Datum): any => {
      let value: any = lookupProperty(datum, column.field);
      if(value == undefined ){
        return undefined;
      } else {
        let referencedPropertyDefinition = column.probeOptions.table.mappers[ value ];
        return referencedPropertyDefinition.value(datum);
      }
    }
  }
}

export const valueExportFactories: { [key: string]: (column: ColumnDefinition) => (datum: Datum) => any } = {
  'instant': (column: ColumnDefinition): (datum: Datum) => Date => {
    return (datum: Datum) => { let value: number = lookupProperty(datum, column.field); return value == undefined ? undefined : new Date(value); };
  },
  'duration': (column: ColumnDefinition): (datum: Datum) => number => {
    let precision = column.probeOptions.durationPrecision ? column.probeOptions.durationPrecision : DurationPrecision.minutes;
    let toDuration = (value: number): number => {
      let duration = millisToDuration(value, precision);
      switch (precision) {
        case DurationPrecision.hours: return duration.hours;
        case DurationPrecision.minutes: return duration.hours * 60 + duration.minutes;
        case DurationPrecision.seconds: return duration.hours * 3600 + duration.minutes * 60 + duration.seconds;
        default: return duration.hours * 3600000 + duration.minutes * 60000 + duration.seconds * 1000 +duration.milliseconds;
      }
    }
    return (datum: Datum) => { let value: number = lookupProperty(datum, column.field); return value == undefined ? undefined : toDuration(value) };
  },
  'boolean': (column: ColumnDefinition): (datum: Datum) => boolean => {
    return (datum: Datum) => { let value: boolean = lookupProperty(datum, column.field); return value };
  },
  'enum': (column: ColumnDefinition): (datum: Datum) => string => {
    return (datum: Datum) => { let value: any = lookupProperty(datum, column.field); return value == undefined ? undefined : (<any>column.enumLabels)[value] != undefined ? '' + (<any>column.enumLabels)[value] : '' + value };
  },
  'any': (column: ColumnDefinition): (datum: Datum) => string => {
    return (datum: Datum) => { let value: any = lookupProperty(datum, column.field); return value == undefined ? undefined : '' + value };
  },
}

export const valueDisplayFactories: { [key: string]: (column: ColumnDefinition) => (datum: Datum) => string } = {
  'instant': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    let format = column.probeOptions.dateFormat ? column.probeOptions.dateFormat : { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    let dateTimeFormatOptions: Intl.DateTimeFormatOptions  =
      typeof format === 'object' ?
        <Intl.DateTimeFormatOptions>format
        : format === 'L'
          ? { year: 'numeric', month: 'numeric', day: 'numeric'  }
          : format === 'LT'
            ? { hour: 'numeric', minute: 'numeric' }
            : { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    return (datum: Datum) => { let value: number = lookupProperty(datum, column.field); return value == undefined ? defaultLabel : getDateTimeFormat(dateTimeFormatOptions).format(value) };
  },
  'duration': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    let precision = column.probeOptions.durationPrecision ? column.probeOptions.durationPrecision : DurationPrecision.minutes;
    let toDurationLabel = (value: number): string => {
      let duration = durationToStrings( millisToDuration(value, precision) );
      switch (precision) {
        case DurationPrecision.hours: return `${duration.hours}`;
        case DurationPrecision.minutes: return `${duration.hours}${timeSeparatorSymbol}${duration.minutes}`;
        case DurationPrecision.seconds: return `${duration.hours}${timeSeparatorSymbol}${duration.minutes}${timeSeparatorSymbol}${duration.seconds}`;
        default: return `${duration.hours}${timeSeparatorSymbol}${duration.minutes}${timeSeparatorSymbol}${duration.seconds}${subSecondSeparatorSymbol}${duration.millis}`;
      }
    }
    return (datum: Datum) => { let value: number = lookupProperty(datum, column.field); return value == undefined ? defaultLabel : toDurationLabel(value) };
  },
  'boolean': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    let trueLabel = column.probeOptions.table.trueLabel ? column.probeOptions.table.trueLabel : '<span class="icon-ok"></span>';
    let falseLabel = column.probeOptions.table.falseLabel ? column.probeOptions.table.falseLabel : '<span class="icon-cancel"></span>';
    return (datum: Datum) => { let value: boolean = lookupProperty(datum, column.field); return value == undefined ? defaultLabel : value ? trueLabel : falseLabel };
  },
  'enum': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    return (datum: Datum) => { let value: any = lookupProperty(datum, column.field); return value == undefined ? defaultLabel : (<any>column.enumLabels)[value] != undefined ? '' + (<any>column.enumLabels)[value] : '' + value };
  },
  'any': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    return (datum: Datum) => { let value: any = lookupProperty(datum, column.field); return value == undefined ? defaultLabel : '' + value };
  },
  'dependent': (column: ColumnDefinition): (datum: Datum) => string => {
    let defaultLabel = column.defaultContent ? column.defaultContent : NO_CONTENT_LABEL;
    return (datum: Datum) => {
      let value: any = lookupProperty(datum, column.field);
      if(value == undefined ){
        return defaultLabel
      } else {
        let referencedPropertyDefinition = column.probeOptions.table.mappers[ value ];
        return referencedPropertyDefinition.display(datum);
      }
    }
  }
}

const createTabulatorFormatter = (valueToLabel: (datum: Datum) => string): (cell: CellComponent, fomatterParams: any, _onRendered: EmptyCallback) => string | HTMLElement => {
  return (cell:  CellComponent, _fomatterParams: any, _onRendered: EmptyCallback) => {
    return valueToLabel(<Datum>cell.getData());
  }
}

export const tabulatorColumnDefinitionFactories: { [key: string]: (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string) => TabulatorColumnDefinition } = {
  'instant': (column: ColumnDefinition, valueToDisplay: (value: any) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      sorter: 'number',
      sorterParams: {
        alignEmptyValues: 'bottom'
      },
      width: 160,
      minWidth: 160,
      formatter: createTabulatorFormatter( valueToDisplay ? valueToDisplay : valueDisplayFactories['instant'](column) )
    }
  },
  'duration': (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      sorter: 'number',
      sorterParams: {
        alignEmptyValues: 'top'
      },
      width: 90,
      minWidth: 90,
      formatter: createTabulatorFormatter( valueToDisplay ? valueToDisplay : valueDisplayFactories['duration'](column) )
    }
  },
  'boolean': (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      sorter: 'boolean',
      sorterParams: {
        alignEmptyValues: 'top'
      },
      width: 100,
      minWidth: 100,
      formatter: createTabulatorFormatter( valueToDisplay ? valueToDisplay : valueDisplayFactories['boolean'](column) )
    }
  },
  'enum': (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      //set sorter that sorts on the labels
      sorterParams: {
        alignEmptyValues: 'top'
      },
      formatter: createTabulatorFormatter(valueToDisplay)
    }

  },
  'any': (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      sorterParams: {
        alignEmptyValues: 'top'
      },
      formatter: createTabulatorFormatter( valueToDisplay ? valueToDisplay : valueDisplayFactories['any'](column) )
    }
  },
  'dependent': (column: ColumnDefinition, valueToDisplay: (datum: Datum) => string): TabulatorColumnDefinition => {
    return {
      title: column.title,
      field: column.field,
      sorterParams: {
        alignEmptyValues: 'top'
      },
      formatter: createTabulatorFormatter( valueToDisplay ? valueToDisplay : valueDisplayFactories['dependent'](column) )
    }
  }
}

